import { ChevronRight, Lock } from 'lucide-react'
import { Link, useLocation } from 'react-router'
import { SidebarMenuButton, SidebarMenuItem } from '@/components/sv/sidebar'
import { Badge } from '@/components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useCompanyPath } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type { Pathname } from '@/lib/hooks/use-company-path'
import type { LucideIcon } from 'lucide-react'

type SidebarPopoverItemBase = {
  title: string
  icon: LucideIcon
  hasAccess: boolean
  badge?: string
  isActive?: (pathname: string) => boolean
}

export type SidebarPopoverItem =
  | (SidebarPopoverItemBase & { external?: false; url: Pathname })
  | (SidebarPopoverItemBase & { external: true; url: string })
  | (SidebarPopoverItemBase & { action: true; onClick: () => void })

type Props = {
  label: string
  icon: LucideIcon
  items: SidebarPopoverItem[]
}

export const SidebarPopoverMenu = ({ label, icon: Icon, items }: Props) => {
  const location = useLocation()
  const { getCompanyPath } = useCompanyPath()
  const isAnyActive = items.some((item) => {
    if ('action' in item) {
      return false
    }
    return item.isActive
      ? item.isActive(location.pathname)
      : location.pathname.includes(item.url)
  })

  return (
    <SidebarMenuItem>
      <Popover>
        <PopoverTrigger asChild>
          <SidebarMenuButton tooltip={label} isActive={isAnyActive}>
            <Icon />
            <div className="overflow-hidden flex-1 transition-[max-width] duration-200 ease-linear max-w-40 group-data-[collapsible=icon]:max-w-0">
              <span className="whitespace-nowrap">{label}</span>
            </div>
            <ChevronRight className="opacity-60 transition-[max-width,opacity] duration-200 ease-linear max-w-4 group-data-[collapsible=icon]:max-w-0 group-data-[collapsible=icon]:opacity-0" />
          </SidebarMenuButton>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="end"
          sideOffset={8}
          className="w-56 p-1"
        >
          <div className="px-2 py-1.5 font-display text-xs uppercase tracking-wider text-muted-foreground">
            {label}
          </div>
          <ul className="flex flex-col">
            {items.map((item) => {
              const ItemIcon = item.hasAccess ? item.icon : Lock
              const className = cn(
                'flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                !item.hasAccess && 'opacity-50 pointer-events-none'
              )
              const content = (
                <>
                  <ItemIcon className="size-4 shrink-0" />
                  <span className="truncate">{item.title}</span>
                  {item.badge ? (
                    <Badge
                      variant="secondary"
                      className="ml-auto shrink-0 px-1.5 py-0 text-[10px] font-normal"
                    >
                      {item.badge}
                    </Badge>
                  ) : null}
                </>
              )

              if ('action' in item) {
                return (
                  <li key={item.title}>
                    <button
                      onClick={item.onClick}
                      className={cn(className, 'w-full')}
                    >
                      {content}
                    </button>
                  </li>
                )
              }

              const isActive = item.isActive
                ? item.isActive(location.pathname)
                : location.pathname.includes(item.url)
              const activeClassName = cn(
                className,
                isActive && 'bg-accent text-accent-foreground'
              )

              return (
                <li key={item.title}>
                  {item.external ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={activeClassName}
                    >
                      {content}
                    </a>
                  ) : item.hasAccess ? (
                    <Link
                      to={getCompanyPath({ pathname: item.url })}
                      className={activeClassName}
                    >
                      {content}
                    </Link>
                  ) : (
                    <div className={activeClassName}>{content}</div>
                  )}
                </li>
              )
            })}
          </ul>
        </PopoverContent>
      </Popover>
    </SidebarMenuItem>
  )
}
